import React from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import classnames from 'classnames'

import { normalizeAsUrl, titleize } from 'services/string'
import { Button, IonIcon } from 'controls'
import FooterFilters from 'components/Footer/FooterFilters'
import FeatureFlag from 'components/FeatureFlag'

import { categoryFooterStyles, categoriesFooterStyles } from './styles'

const MIN_LINKS_PER_LIST = 5

function getElementOffset(el) {
  const rect = el.getBoundingClientRect()
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop

  const navBarMenuSize = 200

  return {
    left: rect.left + scrollLeft,
    top: rect.top + scrollTop - navBarMenuSize
  }
}

export const CategoryFooterLink = ({
  label,
  href,
  count,
  hrefConfig = { pathname: '/srp' }
}) => (
  <li className="wrapper-link">
    <Link as={normalizeAsUrl(href)} href={hrefConfig}>
      <a>
        {label} {count > 0 && <span className="count">({count})</span>}{' '}
      </a>
    </Link>
    <style jsx>{categoryFooterStyles}</style>
  </li>
)

export const CategoryFooter = React.memo(
  ({ title, links, isFullOpen, orientation }) => {
    if (!Array.isArray(links) && links.length === 0) {
      return null
    }

    const orderedLinks = links.sort((a, b) => a.label.localeCompare(b.label))

    return (
      <section
        className={classnames('category-footer', orientation, {
          'is-full-open': isFullOpen
        })}
      >
        <h2 className="categoty-title">{titleize(title)}</h2>

        <ul className="category-footer-list">
          {orderedLinks.map((categoryLink, index) => (
            <CategoryFooterLink key={index} {...categoryLink} />
          ))}
        </ul>
        <style jsx>{categoryFooterStyles}</style>
      </section>
    )
  }
)

CategoryFooter.propTypes = {
  title: PropTypes.string,
  isFullOpen: PropTypes.bool,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  links: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      hrefConfig: PropTypes.object.isRequired
    })
  )
}

export const CategoriesFooter = React.memo(
  ({ categories = [], title, orientation, className }) => {
    const showMore = categories.some(
      ({ links }) => links && links.length > MIN_LINKS_PER_LIST
    )
    const [isFullOpen, setFullOpen] = React.useState(!showMore)

    const CategoriesFooterRef = React.useRef(null)

    const moveTo = React.useCallback(
      isOpen => {
        const { top, left } = getElementOffset(CategoriesFooterRef.current)

        window.scrollTo({
          top,
          left,
          behavior: 'smooth'
        })

        setFullOpen(isOpen)
      },
      [setFullOpen, CategoriesFooterRef]
    )

    const isContent =
      categories.length > 0 &&
      categories.reduce(
        (acc, category) => acc || category && category.links.length > 0,
        false
      )

    if (!isContent) {
      return null
    }

    return (
      <div
        ref={CategoriesFooterRef}
        className={classnames('categories-footer', orientation, className)}
      >
        {title && <h4 className="title">{title}</h4>}

        <FeatureFlag name="footer-new-footer-filters">
          <FooterFilters />
        </FeatureFlag>

        <div
          className={classnames('categories-footer-links', {
            'not-general-title': !title
          })}
        >
          {categories.map((category, index) => (
            <CategoryFooter
              key={index}
              {...category}
              isFullOpen={isFullOpen}
              orientation={orientation}
            />
          ))}
        </div>

        {showMore && (
          <div className="controls">
            {isFullOpen ? (
              <Button onClick={() => moveTo(false)}>
                <IonIcon icon="md-remove-circle" />
                Ver menos
              </Button>
            ) : (
              <Button onClick={() => moveTo(true)}>
                <IonIcon icon="md-add-circle" />
                Ver más
              </Button>
            )}
          </div>
        )}

        <style jsx>{categoriesFooterStyles}</style>
      </div>
    )
  }
)

CategoriesFooter.propTypes = {
  title: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.object),
  orientation: PropTypes.oneOf(['horizontal', 'vertical'])
}
